import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
	DeleteOutlined,
	FileDownload,
	ReplayOutlined,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import ActionButton from 'src/components/common/buttons/ActionButton';
import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { setLoading } from 'src/components/common/loading/loadingSlice';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import { IJob } from 'src/features/job/domain/types';
import {
	deleteJob,
	getJob,
	getJobs,
	JOB_LOADING_KEY,
} from 'src/features/job/store/jobThunks';
import { AppDispatch } from 'src/store';
import { RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';
import { snakeToTitleCase } from 'src/utils/formatter';

import CreateDownloadJobSuccessModal from './CreateDownloadJobSuccessModal';

const CreateDownloadJobModal = React.lazy(
	() =>
		import(
			/* webpackChunkName: "CreateDownloadJobModal" */
			'./CreateDownloadJobModal'
		),
);

export const JobsTable: React.FC = () => {
	useEffect(() => {
		import(
			/* webpackChunkName: "CreateDownloadJobModal" */
			'./CreateDownloadJobModal'
		);
	}, []);

	const { siteSlug } = useParams();
	const dispatch = useDispatch<AppDispatch>();

	const paginatedJobList = useSelector(
		(state: RootState) => state.job.paginatedJobList,
	);
	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[JOB_LOADING_KEY],
	);

	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [clickedJob, setClickedJob] = useState<IJob | null>(null);
	const [newJob, setNewJob] = useState<IJob | null>(null);
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const disabledColor = '#00000061';
	const enabledColor = '#000000DE';

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const fetchJobs = () => {
		if (!siteSlug) return;
		const params = buildQueryParams(queryState);

		dispatch(
			getJobs({
				request: params,
				siteSlug: siteSlug,
			}),
		);
	};

	const refreshJob = async (jobId: number) => {
		if (!siteSlug) return;

		await dispatch(
			getJob({
				jobId,
				siteSlug: siteSlug,
			}),
		);
	};

	const handleDownload = (job: IJob) => {
		dispatch(
			setLoading({
				key: JOB_LOADING_KEY,
				loading: true,
			}),
		);

		try {
			window.open(job.content_url, '_blank');
		} catch (error) {
			console.error('Error opening URL:', error);
			snackbar('Something went wrong, please try again.', 'error');
		} finally {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: false,
				}),
			);
			setClickedJob(null);
		}
	};

	const handleDelete = async () => {
		if (!clickedJob?.id || !clickedJob.site_job_id || !siteSlug) return;

		await dispatch(
			deleteJob({
				siteSlug: siteSlug,
				jobId: clickedJob.id,
				onSuccess: async () => {
					await fetchJobs();
					setIsDeleteModalVisible(false);
					snackbar(
						`Download job ${clickedJob?.site_job_id} has been deleted successfully.`,
						'success',
					);
					setClickedJob(null);
				},
			}),
		);
	};

	const columns: IColumn<IJob>[] = [
		{
			key: 'site_job_id',
			label: 'Job ID',
			sortable: true,
			render: (job) => job.site_job_id,
		},
		{
			key: 'total_patients',
			label: 'Total Patients',
			render: (job) => job.total_patients,
			width: '9%',
		},
		{
			key: 'status',
			label: 'Status',
			render: (job) => (
				<Chip
					label={snakeToTitleCase(job.status)}
					variant='outlined'
					size='small'
				/>
			),
		},
		{
			key: 'selected_date_range',
			label: 'Selected Date Range',
			render: (job) => job.selected_date_range ?? <i>{'N/A'}</i>,
			width: '15%',
		},
		{
			key: 'created_by',
			label: 'Created By',
			render: (job) => job.created_by,
		},
		{
			key: 'progress',
			label: 'Progress',
			width: '7%',
			render: (job) => (
				<Box
					sx={{
						position: 'relative',
						display: 'inline-flex',
					}}
				>
					<CircularProgress
						variant='determinate'
						value={job.progress ?? 0}
					/>
					<Box
						sx={{
							top: 0,
							left: 0,
							bottom: 0,
							right: 0,
							position: 'absolute',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Typography
							variant='caption'
							component='div'
							sx={{ color: 'text.secondary' }}
						>{`${Math.round(job.progress ?? 0)}%`}</Typography>
					</Box>
				</Box>
			),
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			width: '15%',
			render: (job) => formatTimestamp(job.created_at),
		},
		{
			key: 'completed_at',
			label: 'Completed At',
			width: '15%',
			render: (job) => formatTimestamp(job.completed_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			align: 'left',
			render: (job) => (
				<Box
					sx={{
						display: 'inline-flex',
					}}
				>
					{ActionButton({
						title: 'Refresh',
						enabled:
							job.status !== null &&
							job.status.toLowerCase() === 'in_progress',
						onClick: () => {
							refreshJob(job.id);
						},
						enabledColor,
						disabledColor,
						children: (
							<ReplayOutlined
								sx={{
									transform: 'scaleX(-1) rotate(-20deg)',
								}}
							/>
						),
					})}

					{ActionButton({
						title: 'Download',
						enabled:
							job.status !== null &&
							job.status.toLowerCase() === 'completed',
						onClick: () => {
							handleDownload(job);
						},
						enabledColor,
						disabledColor,
						children: <FileDownload />,
					})}

					{ActionButton({
						title: 'Delete',
						enabled:
							job.status !== null &&
							['failed', 'completed'].includes(
								job.status.toLowerCase(),
							),
						onClick: () => {
							setIsDeleteModalVisible(true);
							setClickedJob(job);
						},
						enabledColor,
						disabledColor,
						children: <DeleteOutlined />,
					})}
				</Box>
			),
		},
	];

	useEffect(() => {
		fetchJobs();
	}, [queryState, siteSlug]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			<Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography
						variant='h6'
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							color: 'rgba(0, 0, 0, 0.87)',
						}}
					>
						Download Jobs
					</Typography>
					<Box sx={{ width: '231px', height: '36px' }}>
						<PrimaryButton
							variant='contained'
							color='primary'
							size='medium'
							onClick={() => {
								setIsModalOpen(true);
							}}
							disabled={isLoading}
						>
							CREATE DOWNLOAD JOB
						</PrimaryButton>
					</Box>
				</Box>
			</Box>

			<DataTable
				columns={columns}
				data={paginatedJobList?.results ?? []}
				loadingKey={JOB_LOADING_KEY}
				emptyMessage='No jobs found'
				paginationInfo={{
					totalCount: paginatedJobList?.info.count ?? 0,
				}}
				queryState={queryState}
				onQueryChange={setQueryState}
			/>

			<Suspense fallback={null}>
				<CreateDownloadJobModal
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					onSuccess={(data) => {
						setNewJob(data);
						handleCloseModal();
					}}
				/>
			</Suspense>

			<ConfirmationModal
				title={
					<>
						Are you sure you want to delete job <br />
						<strong>{clickedJob?.site_job_id}</strong>?
					</>
				}
				modalContent={
					'This action cannot be undone. All data associated with this job will be permanently deleted.'
				}
				modalOpen={isDeleteModalVisible}
				handleYes={() => {
					handleDelete();
				}}
				loadingKey={JOB_LOADING_KEY}
				handleNo={() => {
					setIsDeleteModalVisible(false);
					setClickedJob(null);
				}}
				width='480px'
			/>

			{newJob && (
				<CreateDownloadJobSuccessModal
					isModalOpen={!!newJob}
					handleModalClose={() => {
						setNewJob(null);
					}}
					newJob={newJob}
				/>
			)}
		</Box>
	);
};
